import { DateTime } from "luxon";
import { BaseSchema, string } from "yup";

import { phoneNumber } from "@/utilities/validators";

import type { LeaveForm } from "./types";

const maybeRequired = <T extends BaseSchema | Array<unknown>>(
  schema: T,
  required: boolean,
) => {
  if (Array.isArray(schema)) {
    return schema;
  }
  if (required) {
    return schema.required("required");
  }
  return schema;
};

export function useValidations(
  form: LeaveForm,
  simplified: boolean,
  requireAll: boolean,
) {
  let rules;
  if (simplified) {
    rules = {
      destination: string().required("required"),
      hostRelationship: maybeRequired(string(), requireAll),
      hostAge: maybeRequired(string().nullable(), requireAll),
      address: maybeRequired(string(), requireAll),
      city: maybeRequired(string(), requireAll),
      state: maybeRequired(string().nullable(), requireAll),
      transport: maybeRequired(string(), requireAll),
      ccParent: maybeRequired(
        string().email("must be a valid email"),
        requireAll,
      ),
      hostphone: maybeRequired(phoneNumber(), requireAll),
      cellphone: maybeRequired(phoneNumber(), requireAll),
      reason: maybeRequired(string(), requireAll),
      postal: maybeRequired(string().nullable(), requireAll),
      driver: maybeRequired(string().nullable(), requireAll),
      companions: maybeRequired(string().nullable(), requireAll),
      departDate: [(v: DateTime) => v !== null || "required"],
      departTime: [
        (v: DateTime) => v !== null || "required",
        (v: DateTime) => {
          if (form.departDate == null) {
            return true;
          }

          const adjustedTime = v.set({
            year: form.departDate.year,
            month: form.departDate.month,
            day: form.departDate.day,
          });

          return (
            DateTime.now().plus({ minutes: 5 }) <= adjustedTime ||
            "must be atleast 5 minutes after the current date and time"
          );
        },
      ],
      returnDate: [
        (v: DateTime) => !form.departDate || v !== null || "required",
      ],
      returnTime: [
        (v: DateTime) => !form.departDate || v !== null || "required",
      ],
    };
  } else {
    rules = {
      destination: string().required("required"),
      hostRelationship: string().required("required"),
      hostAge: string().nullable().required("required"),
      address: string().required("required"),
      city: string().required("required"),
      state: string().nullable().required("required"),
      transport: string().required("required"),
      postal: maybeRequired(string().nullable(), requireAll),
      driver: maybeRequired(string().nullable(), requireAll),
      companions: maybeRequired(string().nullable(), requireAll),
      ccParent: maybeRequired(
        string().email("must be a valid email"),
        requireAll,
      ),
      hostphone: phoneNumber().required("required"),
      cellphone: phoneNumber().required("required"),
      reason: string().required("required"),
      departDate: [(v: DateTime) => v !== null || "required"],
      departTime: [
        (v: DateTime) => v !== null || "required",
        (v: DateTime) => {
          if (form.departDate == null) {
            return true;
          }

          const adjustedTime = v.set({
            year: form.departDate.year,
            month: form.departDate.month,
            day: form.departDate.day,
          });

          return (
            DateTime.now().plus({ minutes: 5 }) <= adjustedTime ||
            "must be atleast 5 minutes after the current date and time"
          );
        },
      ],
      returnDate: [
        (v: DateTime) => !form.departDate || v !== null || "required",
      ],
      returnTime: [
        (v: DateTime) => !form.departDate || v !== null || "required",
      ],
    };
  }

  return { rules };
}
